<script type="ts">
    import { AccordionItem, Button, FileUploaderButton, FormGroup, Slider, Toggle } from "carbon-components-svelte";
    import TrashCan16 from "carbon-icons-svelte/lib/TrashCan16";
    import { reduce } from "../helpers";
    import type SceneManager from "../scene-manager";
    import type { BannerConfig, BannerPositions } from "../scene-manager";
    import { stores } from "@nubolab-ffwd/svelte-fluent/src/internal/FluentProvider.svelte";

    export let manager:SceneManager;
    export let position:BannerPositions;
    export let label:string;

	const { getTranslation } = stores();

    let banner: BannerConfig;
    let textureAspect: [number, number];
    let textureResolution: [number, number];
    let maxOffset: number;
    let minOffset: number;
    const dpcm = 2;

    const defaultBanner = {
        width: 20,
        height: 20,
        show: false,
        offset: 0,
        textureOffset: 0,
        texture2Offset: 0,
    }

    $: {
        banner = manager.banner[position];
        textureAspect = reduce(position === "center" ? Math.round(banner?.width * Math.PI) : banner?.width, banner?.height);
        textureResolution = [(position === "center" ? Math.round(banner?.width * Math.PI) : banner?.width) * dpcm, banner?.height * dpcm];
    }
    $: maxWidth = (position === "center" 
        ? Math.min(manager.width, manager.depth, 8) 
        : (position === "front" || position === "back") 
            ? manager.width 
            : manager.depth
        ) * 100;
    $: maxHeight = Math.max(0, manager.trussHeight - manager.height);
    $: {
        maxOffset = ((position === "center" 
            ? Math.max(manager.width, manager.depth) 
            : (position === "front" || position === "back") ? manager.width : manager.depth) * 100 - (banner?.width ?? defaultBanner.width)) / 2;
        minOffset = -maxOffset;
    }

    function handleChange(position: BannerPositions, prop: "width" | "height" | "show" | "texture" | "texture2" | "offset" | "textureOffset" | "texture2Offset", value: number | boolean | string) {
        const b = manager.banner;
        manager.banner = {
            ...b,
            [position]: {
                ...defaultBanner,
                ...b[position],
                [prop]: value
            }
        };
    }

    function handleTexture(position: BannerPositions, key: "texture" | "texture2", event) {
        const file = event.currentTarget.files[0];
        const texture = manager.putTexture(file);
        const t = manager.banner[position][key];

        handleChange(position, key, texture);

        if(t) manager.removeTexture(t);
    }

    function clearTexture(position: BannerPositions, key: "texture" | "texture2") {
        const t = manager.banner[position][key];
        handleChange(position, key, undefined);
        manager.removeTexture(t)
    }

</script>

<AccordionItem title={label}>
    <FormGroup>
        <Toggle labelText={$getTranslation("banner-show").value} on:toggle={e => handleChange(position, "show", e.detail.toggled)} labelA={$getTranslation("banner-no").value} labelB={$getTranslation("banner-yes").value} toggled={!!banner?.show} />
    </FormGroup>
    <FormGroup>
        <Slider
            light
            labelText={$getTranslation("banner-width").value}
            min={100}
            max={maxWidth}
            minLabel="100cm"
            maxLabel={`${maxWidth}cm`}
            step={50}
            value={banner?.width ?? defaultBanner.width}
            on:change={(e) => handleChange(position, "width", e.detail)}
        />
        <Slider
            light
            labelText={$getTranslation("banner-height").value}
            min={100}
            max={maxHeight}
            minLabel="100cm"
            maxLabel={`${maxHeight}cm`}
            step={50}
            value={banner?.height ?? defaultBanner.height}
            on:change={(e) => handleChange(position, "height", e.detail)}
        />
        <Slider
            light
            labelText={$getTranslation("banner-position").value}
            min={minOffset}
            max={maxOffset}
            minLabel={`${minOffset}cm`}
            maxLabel={`${maxOffset}cm`}
            step={25}
            value={banner?.offset ?? defaultBanner.offset}
            on:change={(e) => handleChange(position, "offset", e.detail)}
        />
    </FormGroup>
    <FormGroup legendText={$getTranslation("banner-texture").value}>
        <div class="filepicker">
            <FileUploaderButton
                labelText={banner?.texture ? banner.texture : $getTranslation("banner-select-file").value}
                accept={['.jpg', '.jpeg', '.png']}
                on:change={e => handleTexture(position, "texture", e)}
            />
            {#if banner?.texture}
            <Button kind="ghost" iconDescription={$getTranslation("banner-delete").value} icon={TrashCan16} on:click={() => clearTexture(position, "texture")} />
            {/if}
        </div>
        <p>
            <strong>{$getTranslation("banner-texture-comment", { aspect: textureAspect.join(':'), resolution: textureResolution.join('x') }).value}</strong>
        </p>
        {#if position === "center"}
        <Slider
            light
            labelText={$getTranslation("banner-shift").value}
            min={0}
            max={100}
            minLabel={`0%`}
            maxLabel={`100%`}
            step={1}
            value={banner?.textureOffset ?? defaultBanner.textureOffset}
            on:change={(e) => handleChange(position, "textureOffset", e.detail)}
        />
        {/if}
    </FormGroup>
    <FormGroup legendText={$getTranslation("banner-texture2").value}>
        <div class="filepicker">
            <FileUploaderButton
                labelText={banner?.texture2 ? banner.texture2 : $getTranslation("banner-select-file").value}
                accept={['.jpg', '.jpeg', '.png']}
                on:change={e => handleTexture(position, "texture2", e)}
            />
            {#if banner?.texture2}
            <Button kind="ghost" iconDescription={$getTranslation("banner-delete").value} icon={TrashCan16} on:click={() => clearTexture(position, "texture2")} />
            {/if}
        </div>
        <p>
            <strong>{$getTranslation("banner-texture-comment", { aspect: textureAspect.join(':'), resolution: textureResolution.join('x') }).value}</strong>
        </p>
        {#if position === "center"}
        <Slider
            light
            labelText={$getTranslation("banner-shift").value}
            min={0}
            max={100}
            minLabel={`0%`}
            maxLabel={`100%`}
            step={1}
            value={banner?.texture2Offset ?? defaultBanner.texture2Offset}
            on:change={(e) => handleChange(position, "texture2Offset", e.detail)}
        />
        {/if}
    </FormGroup>
</AccordionItem>


<style type="text/scss">
    p {
        font-size: .875rem;
        padding: .25rem 0;
    }

    .filepicker {
        display: flex;

        &--two {
            margin-top: .5rem;
        }
    }
</style>